/**
 * Environment variables validation and configuration
 * Ensures all required environment variables are present and valid
 */

interface EnvironmentConfig {
  VITE_TENANT_BOOTSTRAP_API_ENDPOINT_URL: string;
  VITE_TENANT_DOMAIN: string;
  VITE_COGNITO_USER_POOL_DOMAIN: string;
  VITE_API_TENANT_ENDPOINT: string;
  VITE_API_MONUMENTA_ENDPOINT: string;
  VITE_API_WORKFORCE_ENDPOINT: string;
  VITE_API_USER_ENDPOINT: string;
  VITE_API_PAYROLL_ENDPOINT: string;
}

interface ValidationRule {
  required: boolean | ((isDev: boolean) => boolean);
  validator?: (value: string) => boolean;
  errorMessage?: string;
}

const validationRules: Partial<Record<keyof EnvironmentConfig, ValidationRule>> = {
  VITE_TENANT_BOOTSTRAP_API_ENDPOINT_URL: {
    required: (isDev) => isDev, // Only required in development/localhost
    validator: (value: string) => value.startsWith('https://') && value.includes('amazonaws.com'),
    errorMessage: 'Must be a valid AWS API Gateway URL starting with https://'
  },
  VITE_TENANT_DOMAIN: {
    required: (isDev) => isDev, // Only required in development/localhost
    validator: (value: string) => /^([a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}(\.[a-zA-Z]{2,})?$/.test(value),
    errorMessage: 'Must be a valid domain name'
  },
};

class EnvironmentValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'EnvironmentValidationError';
  }
}

/**
 * Validates a single environment variable
 */
function validateEnvironmentVariable(
  key: keyof EnvironmentConfig,
  value: string | undefined,
  rule: ValidationRule,
  isDev: boolean
): string {
  // Determine if the field is required based on environment
  const isRequired = typeof rule.required === 'function' 
    ? rule.required(isDev) 
    : rule.required;

  // Check if required variable is missing
  if (isRequired && !value) {
    throw new EnvironmentValidationError(
      `Missing required environment variable: ${key}`
    );
  }

  // If value exists and there's a validator, run it
  if (value && rule.validator && !rule.validator(value)) {
    throw new EnvironmentValidationError(
      `Invalid value for ${key}: ${rule.errorMessage || 'Validation failed'}`
    );
  }

  return value || '';
}

/**
 * Validates all environment variables and returns a typed configuration object
 * Throws EnvironmentValidationError if any validation fails
 */
export function validateEnvironment(): EnvironmentConfig {
  const errors: string[] = [];
  const warnings: string[] = [];

  const config = {} as EnvironmentConfig;
  const isDev = import.meta.env.DEV || import.meta.env.MODE === 'development';

  // Validate each environment variable
  for (const [key, rule] of Object.entries(validationRules)) {
    try {
      const envKey = key as keyof EnvironmentConfig;
      const value = import.meta.env[envKey];
      config[envKey] = validateEnvironmentVariable(envKey, value, rule, isDev);
    } catch (error) {
      if (error instanceof EnvironmentValidationError) {
        errors.push(error.message);
      } else {
        errors.push(`Unexpected error validating ${key}: ${String(error)}`);
      }
    }
  }

  // If there are any errors, throw them all at once with helpful setup instructions
  if (errors.length > 0) {
    const setupInstructions = `
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
⚠️  ENVIRONMENT CONFIGURATION MISSING
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

To set up your environment:

1. Copy the example environment file:
   cp .env.example .env

2. Edit .env and replace placeholder values with actual configuration

3. Get the configuration values from your team lead or AWS console

4. Restart the development server after updating .env

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
`;

    console.error(setupInstructions);
    throw new EnvironmentValidationError(
      `Environment validation failed:\n${errors.map(err => `  - ${err}`).join('\n')}`
    );
  }

  return config;
}

/**
 * Get validated environment configuration
 * This should be called once at app startup
 */
export function getEnvironmentConfig(): EnvironmentConfig {
  try {
    return validateEnvironment();
  } catch (error) {
    console.error('❌ Environment validation failed:', error);

    // In development, show detailed error
    if (import.meta.env.DEV) {
      console.error('\n🔧 Development mode: Please check your .env file\n');
      console.error('Expected environment variables:');
      Object.keys(validationRules).forEach(key => {
        const rule = validationRules[key as keyof EnvironmentConfig];
        console.error(`  - ${key}: ${rule?.errorMessage || 'Required'}`);
      });
    }

    throw error;
  }
}

/**
 * Safely get environment variable with fallback
 */
export function getEnvVar(key: keyof EnvironmentConfig, fallback?: string): string {
  const value = import.meta.env[key];
  if (!value && !fallback) {
    console.warn(`⚠️ Environment variable ${key} is not set and no fallback provided`);
  }
  return value || fallback || '';
}

/**
 * Check if we're in development mode
 */
export function isDevelopment(): boolean {
  return import.meta.env.DEV || import.meta.env.MODE === 'development';
}

/**
 * Check if we're in production mode
 */
export function isProduction(): boolean {
  return import.meta.env.PROD || import.meta.env.MODE === 'production';
}

// Export the validated config as a singleton
let validatedConfig: EnvironmentConfig | null = null;

export function getValidatedConfig(): EnvironmentConfig {
  if (!validatedConfig) {
    validatedConfig = getEnvironmentConfig();
  }
  return validatedConfig;
}